/**
 * `/admin/users/:id` — admin edit view for a single user. All user-record
 * fields plus credit/invite balances are directly editable.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AdminUserDetail, AdminUserUpdateRequest, UserRole } from '@eloquor/api-contract';
import { getUserDetail, updateUser } from '../../api/admin';
import { ApiCallError } from '../../api/client';

const ROLES: UserRole[] = ['user', 'admin', 'ephemeral_demo'];

interface FormState {
  displayName: string;
  handle: string;
  email: string;
  role: UserRole;
  weeklyCardGrant: string;
  defaultConcurrency: string;
  creditsAvailable: string;
  invitesAvailable: string;
}

function toForm(d: AdminUserDetail): FormState {
  return {
    displayName: d.user.displayName,
    handle: d.user.handle ?? '',
    email: d.user.email,
    role: d.user.role,
    weeklyCardGrant: String(d.user.weeklyCardGrant),
    defaultConcurrency: String(d.defaultConcurrency),
    creditsAvailable: String(d.credits.available),
    invitesAvailable: String(d.invites.available),
  };
}

function diff(form: FormState, detail: AdminUserDetail): AdminUserUpdateRequest {
  const patch: AdminUserUpdateRequest = {};
  if (form.displayName !== detail.user.displayName) patch.displayName = form.displayName;
  const handleNext = form.handle.trim() ? form.handle.trim() : null;
  if (handleNext !== detail.user.handle) patch.handle = handleNext;
  if (form.email.toLowerCase() !== detail.user.email.toLowerCase()) patch.email = form.email;
  if (form.role !== detail.user.role) patch.role = form.role;
  const wcg = Number.parseInt(form.weeklyCardGrant, 10);
  if (Number.isFinite(wcg) && wcg !== detail.user.weeklyCardGrant) patch.weeklyCardGrant = wcg;
  const dc = Number.parseInt(form.defaultConcurrency, 10);
  if (Number.isFinite(dc) && dc !== detail.defaultConcurrency) patch.defaultConcurrency = dc;
  const ca = Number.parseInt(form.creditsAvailable, 10);
  if (Number.isFinite(ca) && ca !== detail.credits.available) patch.creditsAvailable = ca;
  const ia = Number.parseInt(form.invitesAvailable, 10);
  if (Number.isFinite(ia) && ia !== detail.invites.available) patch.invitesAvailable = ia;
  return patch;
}

export function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const d = await getUserDetail(id);
      setDetail(d);
      setForm(toForm(d));
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not load user.');
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!id || !form || !detail) return;
    setError(null);
    setSuccess(null);
    const patch = diff(form, detail);
    if (Object.keys(patch).length === 0) {
      setSuccess('Nothing to save.');
      return;
    }
    setSaving(true);
    try {
      const d = await updateUser(id, patch);
      setDetail(d);
      setForm(toForm(d));
      setSuccess('Saved.');
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  if (!detail || !form) {
    return (
      <>
        <div className="auth-eyebrow"><span className="num">ADMIN</span> · User</div>
        {error && <div className="banner danger">{error}</div>}
        {!error && <p style={{ color: 'var(--fg-3)' }}>Loading…</p>}
      </>
    );
  }

  const labelStyle = { display: 'block', fontSize: 11, color: 'var(--fg-3)', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: 4 };
  const fieldStyle = { marginBottom: 16, maxWidth: 480 };

  return (
    <>
      <div className="auth-eyebrow">
        <Link to="/admin/users" style={{ color: 'inherit' }}>
          <span className="num">ADMIN</span> · Users
        </Link>{' '}
        · {detail.user.displayName}
      </div>
      <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 32, margin: '4px 0 16px' }}>
        {detail.user.displayName}
      </h1>

      <div style={{ ...fieldStyle, color: 'var(--fg-2)', fontSize: 13 }}>
        <span style={labelStyle}>Invited by</span>
        {detail.invitedBy ? (
          <Link
            to={`/admin/users/${encodeURIComponent(detail.invitedBy.id)}`}
            style={{ color: 'var(--fg-1)' }}
          >
            {detail.invitedBy.displayName}{' '}
            <span style={{ color: 'var(--fg-3)' }}>({detail.invitedBy.email})</span>
          </Link>
        ) : (
          <span style={{ color: 'var(--fg-3)' }}>—</span>
        )}
      </div>

      <div style={{ ...fieldStyle, fontSize: 13, color: 'var(--fg-3)' }}>
        <span style={labelStyle}>ID</span>
        <span className="mono">{detail.user.id}</span>
      </div>

      {error && <div className="banner danger">{error}</div>}
      {success && <div className="banner">{success}</div>}

      <form onSubmit={handleSubmit}>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="displayName">Display name</label>
          <input
            className="input"
            id="displayName"
            type="text"
            value={form.displayName}
            onChange={(e) => set('displayName', e.target.value)}
            required
          />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="handle">Handle</label>
          <input
            className="input"
            id="handle"
            type="text"
            value={form.handle}
            onChange={(e) => set('handle', e.target.value)}
            placeholder="(none)"
          />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="email">Email</label>
          <input
            className="input"
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            required
          />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="role">Role</label>
          <select
            className="input"
            id="role"
            value={form.role}
            onChange={(e) => set('role', e.target.value as UserRole)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="weeklyCardGrant">Weekly card grant</label>
          <input
            className="input"
            id="weeklyCardGrant"
            type="number"
            min={0}
            value={form.weeklyCardGrant}
            onChange={(e) => set('weeklyCardGrant', e.target.value)}
          />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="defaultConcurrency">Default concurrency</label>
          <input
            className="input"
            id="defaultConcurrency"
            type="number"
            min={1}
            max={64}
            value={form.defaultConcurrency}
            onChange={(e) => set('defaultConcurrency', e.target.value)}
          />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="creditsAvailable">
            Credits available <span style={{ color: 'var(--fg-3)' }}>(reserved: {detail.credits.reserved})</span>
          </label>
          <input
            className="input"
            id="creditsAvailable"
            type="number"
            min={0}
            value={form.creditsAvailable}
            onChange={(e) => set('creditsAvailable', e.target.value)}
          />
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="invitesAvailable">
            Invites available <span style={{ color: 'var(--fg-3)' }}>({detail.invites.lifetimeGranted} ever granted)</span>
          </label>
          <input
            className="input"
            id="invitesAvailable"
            type="number"
            min={0}
            value={form.invitesAvailable}
            onChange={(e) => set('invitesAvailable', e.target.value)}
          />
        </div>

        <button className="btn-primary" type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </>
  );
}
